import RAPIER from "@dimforge/rapier3d-compat";
import * as THREE from "three";
import { CollisionLayer, interactionGroups } from "../physics/layers";
import { PALETTE, disposeObject, standardMaterial } from "../render/materials";

export const VEGETATION_STRIDE = 7 as const;
export const VegetationKind = { broadleaf: 0, conifer: 1, shrub: 2 } as const;
export type VegetationKind = (typeof VegetationKind)[keyof typeof VegetationKind];

export interface IndexedParkRing {
  start: number;
  end: number;
  minX: number;
  minZ: number;
  maxX: number;
  maxZ: number;
}

export interface VegetationTileInput {
  id: string;
  tileX: number;
  tileZ: number;
  tileSizeM: number;
  buildings: Float32Array;
  roads: Float32Array;
}

export interface VegetationSnapshot {
  trees: number;
  shrubs: number;
  activeColliders: number;
}

interface SampleVegetationInput {
  tileX: number;
  tileZ: number;
  tileSizeM: number;
  parkBoundaries: Float32Array;
  parkRings: readonly IndexedParkRing[];
  buildings: Float32Array;
  roads: Float32Array;
}

interface TreeRecord {
  key: string;
  x: number;
  z: number;
  trunkHeight: number;
  trunkRadius: number;
}

interface VegetationTile {
  group: THREE.Group;
  trees: TreeRecord[];
  treeCount: number;
  shrubCount: number;
}
export interface PreparedVegetationTile {
  readonly id: string;
  readonly generation: number;
  readonly root: THREE.Group;
}

export interface RetiredVegetationTile {
  readonly id: string;
}

interface PreparedVegetationRecord {
  handle: PreparedVegetationTile;
  tile: VegetationTile;
}

interface RetiredVegetationRecord {
  tile: VegetationTile;
  colliders: RAPIER.Collider[];
}

const TREE_GRID_M = 18;
const SHRUB_GRID_M = 10;
const COLLIDER_RADIUS_M = 45;
const COLLIDER_RADIUS_SQUARED = COLLIDER_RADIUS_M * COLLIDER_RADIUS_M;
const COLLIDER_REFRESH_SECONDS = 0.25;
const MAX_TREE_COLLIDERS = 128;
const VEGETATION_COLORS = [0x244434, 0x356044, 0x4b753e] as const;

function unitHash(cellX: number, cellZ: number, salt: number): number {
  let h = Math.imul(cellX, 73_856_093) ^ Math.imul(cellZ, 19_349_663) ^ Math.imul(salt, 83_492_791);
  h ^= h >>> 16;
  return (h >>> 0) / 0x1_0000_0000;
}

function distanceSquaredToSegment(
  x: number,
  z: number,
  fromX: number,
  fromZ: number,
  toX: number,
  toZ: number,
): number {
  const deltaX = toX - fromX;
  const deltaZ = toZ - fromZ;
  const lengthSquared = deltaX * deltaX + deltaZ * deltaZ;
  if (lengthSquared === 0) return (x - fromX) ** 2 + (z - fromZ) ** 2;
  const t = Math.max(0, Math.min(1, ((x - fromX) * deltaX + (z - fromZ) * deltaZ) / lengthSquared));
  const nearestX = fromX + t * deltaX;
  const nearestZ = fromZ + t * deltaZ;
  return (x - nearestX) ** 2 + (z - nearestZ) ** 2;
}

function ringContainsWithClearance(
  x: number,
  z: number,
  clearance: number,
  boundaries: Float32Array,
  ring: IndexedParkRing,
): boolean {
  if (x < ring.minX || x > ring.maxX || z < ring.minZ || z > ring.maxZ || ring.end - ring.start < 6)
    return false;

  let inside = false;
  let minimumEdgeDistanceSquared = Number.POSITIVE_INFINITY;
  let previousOffset = ring.end - 2;
  for (let offset = ring.start; offset < ring.end; offset += 2) {
    const pointX = boundaries[offset] ?? 0;
    const pointZ = boundaries[offset + 1] ?? 0;
    const previousX = boundaries[previousOffset] ?? 0;
    const previousZ = boundaries[previousOffset + 1] ?? 0;
    if (
      pointZ > z !== previousZ > z &&
      x < ((previousX - pointX) * (z - pointZ)) / (previousZ - pointZ) + pointX
    )
      inside = !inside;
    minimumEdgeDistanceSquared = Math.min(
      minimumEdgeDistanceSquared,
      distanceSquaredToSegment(x, z, pointX, pointZ, previousX, previousZ),
    );
    previousOffset = offset;
  }
  return inside && minimumEdgeDistanceSquared >= clearance * clearance;
}

function clearsRoads(x: number, z: number, roads: Float32Array): boolean {
  for (let offset = 0; offset < roads.length; offset += 5) {
    const clearance = (roads[offset + 4] ?? 0) / 2 + 2;
    if (
      distanceSquaredToSegment(
        x,
        z,
        roads[offset] ?? 0,
        roads[offset + 1] ?? 0,
        roads[offset + 2] ?? 0,
        roads[offset + 3] ?? 0,
      ) <
      clearance * clearance
    )
      return false;
  }
  return true;
}

function clearsBuildings(x: number, z: number, buildings: Float32Array): boolean {
  for (let offset = 0; offset < buildings.length; offset += 5) {
    const centerX = buildings[offset] ?? 0;
    const centerZ = buildings[offset + 1] ?? 0;
    const halfWidth = (buildings[offset + 2] ?? 0) / 2 + 2;
    const halfDepth = (buildings[offset + 3] ?? 0) / 2 + 2;
    if (Math.abs(x - centerX) <= halfWidth && Math.abs(z - centerZ) <= halfDepth) return false;
  }
  return true;
}

function sampleGrid(
  output: number[],
  input: SampleVegetationInput,
  gridSize: number,
  occupancy: number,
  jitter: number,
  kind: "tree" | "shrub",
): void {
  const tileMinX = input.tileX * input.tileSizeM;
  const tileMinZ = input.tileZ * input.tileSizeM;
  const tileMaxX = tileMinX + input.tileSizeM;
  const tileMaxZ = tileMinZ + input.tileSizeM;
  const firstCellX = Math.ceil(tileMinX / gridSize - 0.5);
  const firstCellZ = Math.ceil(tileMinZ / gridSize - 0.5);
  const lastCellX = Math.ceil(tileMaxX / gridSize - 0.5);
  const lastCellZ = Math.ceil(tileMaxZ / gridSize - 0.5);
  const clearance = kind === "tree" ? 2 : 0.8;

  for (let cellZ = firstCellZ; cellZ < lastCellZ; cellZ += 1) {
    for (let cellX = firstCellX; cellX < lastCellX; cellX += 1) {
      if (unitHash(cellX, cellZ, 0) >= occupancy) continue;
      const x = (cellX + 0.5) * gridSize + (unitHash(cellX, cellZ, 1) * 2 - 1) * jitter;
      const z = (cellZ + 0.5) * gridSize + (unitHash(cellX, cellZ, 2) * 2 - 1) * jitter;
      let inPark = false;
      for (const ring of input.parkRings) {
        if (ringContainsWithClearance(x, z, clearance, input.parkBoundaries, ring)) {
          inPark = true;
          break;
        }
      }
      if (!inPark || !clearsRoads(x, z, input.roads) || !clearsBuildings(x, z, input.buildings))
        continue;

      const sampledKind =
        kind === "shrub"
          ? VegetationKind.shrub
          : unitHash(cellX, cellZ, 3) < 0.75
            ? VegetationKind.broadleaf
            : VegetationKind.conifer;
      const heightUnit = unitHash(cellX, cellZ, 4);
      const radiusUnit = unitHash(cellX, cellZ, 5);
      const height = kind === "tree" ? 6 + heightUnit * 8 : 0.7 + heightUnit * 0.9;
      const radius = kind === "tree" ? 2.2 + radiusUnit * 2 : 0.8 + radiusUnit * 0.8;
      const rotation = unitHash(cellX, cellZ, 6) * Math.PI * 2;
      const colorVariant = Math.min(2, Math.floor(unitHash(cellX, cellZ, 7) * 3));
      output.push(x, z, height, radius, rotation, sampledKind, colorVariant);
    }
  }
}

export function sampleVegetationTile(input: SampleVegetationInput): Float32Array {
  if (input.parkRings.length === 0) return new Float32Array();
  const records: number[] = [];
  sampleGrid(records, input, TREE_GRID_M, 0.8, 5, "tree");
  sampleGrid(records, input, SHRUB_GRID_M, 0.6, 3, "shrub");
  return Float32Array.from(records);
}

async function sampleVegetationTileIncremental(
  input: SampleVegetationInput,
  checkpoint: () => Promise<void>,
  signal: AbortSignal,
): Promise<Float32Array> {
  if (input.parkRings.length === 0) return new Float32Array();
  const records: number[] = [];
  const sample = async (
    gridSize: number,
    occupancy: number,
    jitter: number,
    kind: "tree" | "shrub",
  ): Promise<void> => {
    const tileMinX = input.tileX * input.tileSizeM;
    const tileMinZ = input.tileZ * input.tileSizeM;
    const tileMaxX = tileMinX + input.tileSizeM;
    const tileMaxZ = tileMinZ + input.tileSizeM;
    const firstCellX = Math.ceil(tileMinX / gridSize - 0.5);
    const firstCellZ = Math.ceil(tileMinZ / gridSize - 0.5);
    const lastCellX = Math.ceil(tileMaxX / gridSize - 0.5);
    const lastCellZ = Math.ceil(tileMaxZ / gridSize - 0.5);
    const clearance = kind === "tree" ? 2 : 0.8;
    for (let cellZ = firstCellZ; cellZ < lastCellZ; cellZ += 1) {
      for (let cellX = firstCellX; cellX < lastCellX; cellX += 1) {
        if (unitHash(cellX, cellZ, 0) >= occupancy) continue;
        const x = (cellX + 0.5) * gridSize + (unitHash(cellX, cellZ, 1) * 2 - 1) * jitter;
        const z = (cellZ + 0.5) * gridSize + (unitHash(cellX, cellZ, 2) * 2 - 1) * jitter;
        let inPark = false;
        for (const ring of input.parkRings) {
          if (ringContainsWithClearance(x, z, clearance, input.parkBoundaries, ring)) {
            inPark = true;
            break;
          }
        }
        if (!inPark || !clearsRoads(x, z, input.roads) || !clearsBuildings(x, z, input.buildings))
          continue;
        const sampledKind =
          kind === "shrub"
            ? VegetationKind.shrub
            : unitHash(cellX, cellZ, 3) < 0.75
              ? VegetationKind.broadleaf
              : VegetationKind.conifer;
        const heightUnit = unitHash(cellX, cellZ, 4);
        const radiusUnit = unitHash(cellX, cellZ, 5);
        records.push(
          x,
          z,
          kind === "tree" ? 6 + heightUnit * 8 : 0.7 + heightUnit * 0.9,
          kind === "tree" ? 2.2 + radiusUnit * 2 : 0.8 + radiusUnit * 0.8,
          unitHash(cellX, cellZ, 6) * Math.PI * 2,
          sampledKind,
          Math.min(2, Math.floor(unitHash(cellX, cellZ, 7) * 3)),
        );
      }
      await checkpoint();
      signal.throwIfAborted();
    }
  };
  await sample(TREE_GRID_M, 0.8, 5, "tree");
  await sample(SHRUB_GRID_M, 0.6, 3, "shrub");
  return Float32Array.from(records);
}

function setInstanceTransform(
  mesh: THREE.InstancedMesh,
  index: number,
  object: THREE.Object3D,
  x: number,
  y: number,
  z: number,
  scaleX: number,
  scaleY: number,
  scaleZ: number,
  rotation: number,
): void {
  object.position.set(x, y, z);
  object.rotation.set(0, rotation, 0);
  object.scale.set(scaleX, scaleY, scaleZ);
  object.updateMatrix();
  mesh.setMatrixAt(index, object.matrix);
}

function rotatedOffset(x: number, z: number, rotation: number): readonly [number, number] {
  const cosine = Math.cos(rotation);
  const sine = Math.sin(rotation);
  return [x * cosine + z * sine, -x * sine + z * cosine];
}

function finishInstances(mesh: THREE.InstancedMesh): void {
  mesh.instanceMatrix.needsUpdate = true;
  if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  mesh.computeBoundingBox();
  mesh.computeBoundingSphere();
}

export class VegetationSystem {
  readonly #world: RAPIER.World;
  readonly #playerBody: RAPIER.RigidBody;
  readonly #ringsByTile = new Map<string, IndexedParkRing[]>();
  readonly #tiles = new Map<string, VegetationTile>();
  readonly #preparedTiles = new Map<string, PreparedVegetationRecord>();
  readonly #retiredTiles = new Map<RetiredVegetationTile, RetiredVegetationRecord>();
  readonly #activeColliders = new Map<string, RAPIER.Collider>();
  #parkBoundaries: Float32Array<ArrayBufferLike> = new Float32Array();
  #colliderElapsed = 0;

  constructor(world: RAPIER.World, playerBody: RAPIER.RigidBody) {
    this.#world = world;
    this.#playerBody = playerBody;
  }

  indexParkland(parkOffsets: Uint32Array, parkBoundaries: Float32Array, tileSizeM: number): void {
    this.#ringsByTile.clear();
    this.#parkBoundaries = parkBoundaries;
    const pointCount = parkBoundaries.length / 2;
    for (let offsetIndex = 0; offsetIndex < parkOffsets.length; offsetIndex += 1) {
      const current = parkOffsets[offsetIndex] ?? 0;
      const previous = offsetIndex === 0 ? 0 : (parkOffsets[offsetIndex - 1] ?? 0);
      if (current > pointCount || current < previous)
        throw new Error("Park ring offsets are invalid");
    }

    for (let ringIndex = 0; ringIndex < parkOffsets.length - 1; ringIndex += 1) {
      const start = (parkOffsets[ringIndex] ?? 0) * 2;
      const end = (parkOffsets[ringIndex + 1] ?? 0) * 2;
      const distinctPoints = new Set<string>();
      let minX = Number.POSITIVE_INFINITY;
      let minZ = Number.POSITIVE_INFINITY;
      let maxX = Number.NEGATIVE_INFINITY;
      let maxZ = Number.NEGATIVE_INFINITY;
      for (let offset = start; offset < end; offset += 2) {
        const x = parkBoundaries[offset] ?? 0;
        const z = parkBoundaries[offset + 1] ?? 0;
        distinctPoints.add(`${x}:${z}`);
        minX = Math.min(minX, x);
        minZ = Math.min(minZ, z);
        maxX = Math.max(maxX, x);
        maxZ = Math.max(maxZ, z);
      }
      if (distinctPoints.size < 3) continue;
      const ring = { start, end, minX, minZ, maxX, maxZ };
      const minTileX = Math.floor(minX / tileSizeM);
      const minTileZ = Math.floor(minZ / tileSizeM);
      const maxTileX = Math.floor(maxX / tileSizeM);
      const maxTileZ = Math.floor(maxZ / tileSizeM);
      for (let tileZ = minTileZ; tileZ <= maxTileZ; tileZ += 1) {
        for (let tileX = minTileX; tileX <= maxTileX; tileX += 1) {
          const key = `${tileX}_${tileZ}`;
          const rings = this.#ringsByTile.get(key) ?? [];
          rings.push(ring);
          this.#ringsByTile.set(key, rings);
        }
      }
    }
  }

  async prepareTile(
    input: VegetationTileInput,
    generation: number,
    checkpoint: () => Promise<void>,
    signal: AbortSignal,
  ): Promise<PreparedVegetationTile> {
    const key = `${generation}:${input.id}`;
    const existing = this.#preparedTiles.get(key);
    if (existing) return existing.handle;
    signal.throwIfAborted();
    await checkpoint();
    signal.throwIfAborted();
    const group = new THREE.Group();
    group.name = `vegetation:${input.id}`;
    try {
      const samples = await sampleVegetationTileIncremental(
        {
          tileX: input.tileX,
          tileZ: input.tileZ,
          tileSizeM: input.tileSizeM,
          parkBoundaries: this.#parkBoundaries,
          parkRings: this.#ringsByTile.get(`${input.tileX}_${input.tileZ}`) ?? [],
          buildings: input.buildings,
          roads: input.roads,
        },
        checkpoint,
        signal,
      );
      await checkpoint();
      signal.throwIfAborted();
      let treeCount = 0;
      let broadleafCount = 0;
      let coniferCount = 0;
      let shrubCount = 0;
      for (let offset = 0; offset < samples.length; offset += VEGETATION_STRIDE) {
        if (offset % (VEGETATION_STRIDE * 64) === 0) {
          await checkpoint();
          signal.throwIfAborted();
        }
        const kind = samples[offset + 5];
        if (kind === VegetationKind.shrub) shrubCount += 1;
        else {
          treeCount += 1;
          if (kind === VegetationKind.broadleaf) broadleafCount += 1;
          else coniferCount += 1;
        }
      }

      const trees: TreeRecord[] = [];
      if (samples.length > 0) {
        const trunkGeometry = new THREE.BoxGeometry(1, 1, 1);
        const foliageGeometry = new THREE.IcosahedronGeometry(1, 0);
        const coniferGeometry = new THREE.ConeGeometry(1, 1, 6);
        const trunkMaterial = standardMaterial(0x4a3426, 1, 0);
        const foliageMaterial = standardMaterial(PALETTE.park, 1, 0);
        const trunks = new THREE.InstancedMesh(trunkGeometry, trunkMaterial, treeCount);
        const broadleaves = new THREE.InstancedMesh(
          foliageGeometry,
          foliageMaterial,
          broadleafCount * 3,
        );
        const conifers = new THREE.InstancedMesh(
          coniferGeometry,
          foliageMaterial,
          coniferCount * 2,
        );
        const shrubs = new THREE.InstancedMesh(foliageGeometry, foliageMaterial, shrubCount * 2);
        group.add(trunks, broadleaves, conifers, shrubs);
        const object = new THREE.Object3D();
        const color = new THREE.Color();
        let trunkIndex = 0;
        let broadleafIndex = 0;
        let coniferIndex = 0;
        let shrubIndex = 0;

        for (let offset = 0; offset < samples.length; offset += VEGETATION_STRIDE) {
          if (offset % (VEGETATION_STRIDE * 64) === 0) {
            await checkpoint();
            signal.throwIfAborted();
          }
          const x = samples[offset] ?? 0;
          const z = samples[offset + 1] ?? 0;
          const height = samples[offset + 2] ?? 0;
          const radius = samples[offset + 3] ?? 0;
          const rotation = samples[offset + 4] ?? 0;
          const kind = samples[offset + 5] ?? VegetationKind.shrub;
          const colorVariant = samples[offset + 6] ?? 0;
          color.setHex(VEGETATION_COLORS[colorVariant] ?? VEGETATION_COLORS[0]);
          if (kind === VegetationKind.shrub) {
            const firstOffset = rotatedOffset(-0.25 * radius, 0, rotation);
            setInstanceTransform(
              shrubs,
              shrubIndex,
              object,
              x + firstOffset[0],
              0.5 * height,
              z + firstOffset[1],
              1.2 * radius,
              height,
              radius,
              rotation,
            );
            shrubs.setColorAt(shrubIndex, color);
            shrubIndex += 1;
            const secondOffset = rotatedOffset(0.25 * radius, 0.12 * radius, rotation);
            setInstanceTransform(
              shrubs,
              shrubIndex,
              object,
              x + secondOffset[0],
              0.43 * height,
              z + secondOffset[1],
              radius,
              0.85 * height,
              1.1 * radius,
              rotation,
            );
            shrubs.setColorAt(shrubIndex, color);
            shrubIndex += 1;
            continue;
          }

          const trunkHeight = 0.42 * height;
          const trunkRadius = Math.max(0.28, Math.min(0.48, 0.11 * radius));
          setInstanceTransform(
            trunks,
            trunkIndex,
            object,
            x,
            trunkHeight / 2,
            z,
            trunkRadius * 2,
            trunkHeight,
            trunkRadius * 2,
            rotation,
          );
          trees.push({
            key: `${input.id}:${trunkIndex}`,
            x,
            z,
            trunkHeight,
            trunkRadius,
          });
          trunkIndex += 1;

          if (kind === VegetationKind.broadleaf) {
            setInstanceTransform(
              broadleaves,
              broadleafIndex,
              object,
              x,
              0.7 * height,
              z,
              1.5 * radius,
              0.34 * height,
              1.5 * radius,
              rotation,
            );
            broadleaves.setColorAt(broadleafIndex, color);
            broadleafIndex += 1;
            const leftOffset = rotatedOffset(-0.42 * radius, 0.18 * radius, rotation);
            setInstanceTransform(
              broadleaves,
              broadleafIndex,
              object,
              x + leftOffset[0],
              0.74 * height,
              z + leftOffset[1],
              1.1 * radius,
              0.28 * height,
              1.1 * radius,
              rotation,
            );
            broadleaves.setColorAt(broadleafIndex, color);
            broadleafIndex += 1;
            const rightOffset = rotatedOffset(0.42 * radius, -0.18 * radius, rotation);
            setInstanceTransform(
              broadleaves,
              broadleafIndex,
              object,
              x + rightOffset[0],
              0.76 * height,
              z + rightOffset[1],
              1.1 * radius,
              0.28 * height,
              1.1 * radius,
              rotation,
            );
            broadleaves.setColorAt(broadleafIndex, color);
            broadleafIndex += 1;
          } else {
            setInstanceTransform(
              conifers,
              coniferIndex,
              object,
              x,
              0.55 * height,
              z,
              radius,
              0.55 * height,
              radius,
              rotation,
            );
            conifers.setColorAt(coniferIndex, color);
            coniferIndex += 1;
            setInstanceTransform(
              conifers,
              coniferIndex,
              object,
              x,
              0.8 * height,
              z,
              0.68 * radius,
              0.42 * height,
              0.68 * radius,
              rotation,
            );
            conifers.setColorAt(coniferIndex, color);
            coniferIndex += 1;
          }
        }

        trunks.castShadow = true;
        trunks.receiveShadow = true;
        broadleaves.castShadow = true;
        broadleaves.receiveShadow = true;
        conifers.castShadow = true;
        conifers.receiveShadow = true;
        shrubs.receiveShadow = true;
        for (const mesh of [trunks, broadleaves, conifers, shrubs]) finishInstances(mesh);
      }

      const tile = { group, trees, treeCount, shrubCount };
      await checkpoint();
      signal.throwIfAborted();
      const handle: PreparedVegetationTile = {
        id: input.id,
        generation,
        root: group,
      };
      this.#preparedTiles.set(key, { handle, tile });
      return handle;
    } catch (error) {
      disposeObject(group);
      throw error;
    }
  }

  commitPreparedTile(prepared: PreparedVegetationTile): void {
    const key = `${prepared.generation}:${prepared.id}`;
    const record = this.#preparedTiles.get(key);
    if (!record || record.handle !== prepared)
      throw new Error(`Vegetation tile ${key} is not prepared by this system`);
    if (this.#tiles.has(prepared.id))
      throw new Error(`Vegetation tile ${prepared.id} is already active`);
    this.#preparedTiles.delete(key);
    this.#tiles.set(prepared.id, record.tile);
  }

  disposePreparedTile(prepared: PreparedVegetationTile): void {
    const key = `${prepared.generation}:${prepared.id}`;
    const record = this.#preparedTiles.get(key);
    if (!record || record.handle !== prepared) return;
    this.#preparedTiles.delete(key);
    record.tile.group.removeFromParent();
    disposeObject(record.tile.group);
  }

  retireTile(id: string): RetiredVegetationTile | null {
    const tile = this.#tiles.get(id);
    if (!tile) return null;
    this.#tiles.delete(id);
    tile.group.removeFromParent();
    const colliders: RAPIER.Collider[] = [];
    for (const tree of tile.trees) {
      const collider = this.#activeColliders.get(tree.key);
      if (!collider) continue;
      collider.setEnabled(false);
      this.#activeColliders.delete(tree.key);
      colliders.push(collider);
    }
    const retired: RetiredVegetationTile = { id };
    this.#retiredTiles.set(retired, { tile, colliders });
    return retired;
  }

  disposeRetiredTile(retired: RetiredVegetationTile): void {
    const record = this.#retiredTiles.get(retired);
    if (!record) return;
    this.#retiredTiles.delete(retired);
    for (const collider of record.colliders) this.#world.removeCollider(collider, false);
    record.tile.group.removeFromParent();
    disposeObject(record.tile.group);
  }

  async disposeRetiredTileIncremental(
    retired: RetiredVegetationTile,
    checkpoint: () => Promise<void>,
  ): Promise<void> {
    const record = this.#retiredTiles.get(retired);
    if (!record) return;
    this.#retiredTiles.delete(retired);
    for (const collider of record.colliders) {
      this.#world.removeCollider(collider, false);
      await checkpoint();
    }
    record.tile.group.removeFromParent();
    disposeObject(record.tile.group);
    await checkpoint();
  }

  snapshot(): VegetationSnapshot {
    let trees = 0;
    let shrubs = 0;
    for (const tile of this.#tiles.values()) {
      trees += tile.treeCount;
      shrubs += tile.shrubCount;
    }
    return { trees, shrubs, activeColliders: this.#activeColliders.size };
  }

  fixedUpdate(stepSeconds: number): void {
    this.#colliderElapsed += stepSeconds;
    if (this.#colliderElapsed < COLLIDER_REFRESH_SECONDS) return;
    this.#colliderElapsed %= COLLIDER_REFRESH_SECONDS;
    this.#refreshColliders();
  }

  frameUpdate(_alpha: number): void {}

  dispose(): void {
    for (const collider of this.#activeColliders.values())
      this.#world.removeCollider(collider, false);
    this.#activeColliders.clear();
    for (const tile of this.#tiles.values()) {
      tile.group.removeFromParent();
      disposeObject(tile.group);
    }
    this.#tiles.clear();
    for (const { tile } of this.#preparedTiles.values()) {
      tile.group.removeFromParent();
      disposeObject(tile.group);
    }
    this.#preparedTiles.clear();
    for (const { tile, colliders } of this.#retiredTiles.values()) {
      for (const collider of colliders) this.#world.removeCollider(collider, false);
      tile.group.removeFromParent();
      disposeObject(tile.group);
    }
    this.#retiredTiles.clear();
    this.#ringsByTile.clear();
  }

  #refreshColliders(): void {
    const player = this.#playerBody.translation();
    const eligible: { tree: TreeRecord; distanceSquared: number }[] = [];
    for (const tile of this.#tiles.values()) {
      for (const tree of tile.trees) {
        const distanceSquared = (tree.x - player.x) ** 2 + (tree.z - player.z) ** 2;
        if (distanceSquared <= COLLIDER_RADIUS_SQUARED) eligible.push({ tree, distanceSquared });
      }
    }
    eligible.sort((left, right) => left.distanceSquared - right.distanceSquared);
    const desired = new Set(eligible.slice(0, MAX_TREE_COLLIDERS).map(({ tree }) => tree.key));
    for (const [key, collider] of this.#activeColliders) {
      if (desired.has(key)) continue;
      this.#world.removeCollider(collider, false);
      this.#activeColliders.delete(key);
    }
    for (const { tree } of eligible.slice(0, MAX_TREE_COLLIDERS)) {
      if (this.#activeColliders.has(tree.key)) continue;
      const collider = this.#world.createCollider(
        RAPIER.ColliderDesc.cylinder(tree.trunkHeight / 2, tree.trunkRadius)
          .setTranslation(tree.x, tree.trunkHeight / 2, tree.z)
          .setCollisionGroups(interactionGroups(CollisionLayer.WORLD, 0xffff)),
      );
      this.#activeColliders.set(tree.key, collider);
    }
  }
}
